<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <div class="col-12">
        <!-- Card com dados do paciente -->
        <div class="card mb-4">
          <div class="card-body">
            <div class="row align-items-center">
              <div class="col-sm-6">
                <h4 class="card-title mb-0">
                  <i class="bx bx-user me-2"></i>
                  Dados do Paciente
                </h4>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-right">
                  <button class="btn btn-outline-primary btn-sm float-right" @click="goBack">
                    <i class="bx bx-arrow-back"></i> Voltar
                  </button>
                </div>
              </div>
            </div>
            
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
              </div>
            </div>

            <div v-else-if="patient" class="row mt-3">
              <div class="col-md-6">
                <div class="d-flex align-items-center mb-2">
                  <strong class="me-2">Nome:</strong>
                  <span>{{ patient.name || 'Não informado' }}</span>
                </div>
                <div class="d-flex align-items-center mb-2">
                  <strong class="me-2">CPF:</strong>
                  <span>{{ formatCPFDisplay(patient.cpf) || 'Não informado' }}</span>
                </div>
                <div v-if="patient.birth_date" class="d-flex align-items-center mb-2">
                  <strong class="me-2">Data de Nascimento:</strong>
                  <span>{{ formatDate(patient.birth_date) }}</span>
                </div>
              </div>
              <div class="col-md-6">
                <div v-if="patient.email" class="d-flex align-items-center mb-2">
                  <strong class="me-2">Email:</strong>
                  <span>{{ patient.email }}</span>
                </div>
                <div v-if="patient.phone" class="d-flex align-items-center mb-2">
                  <strong class="me-2">Telefone:</strong>
                  <span>{{ patient.phone }}</span>
                </div>
                <div class="d-flex align-items-center mb-2">
                  <strong class="me-2">Total de Exames:</strong>
                  <span class="badge bg-soft-primary text-primary">{{ totalExams }}</span>
                </div>
              </div>
            </div>

            <div v-else class="alert alert-warning">
              <i class="bx bx-info-circle me-2"></i>
              Paciente não encontrado
            </div>
          </div>
        </div>

        <!-- Card com lista de exames -->
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <h4 class="card-title">Exames do Paciente</h4>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-right">
                  
                </div>
              </div>
            </div>

            <!-- Filtros para exames -->
            <div class="row mb-3">
              <div class="col-md-4">
                <select v-model="filters.status" class="form-select">
                  <option value="">Todos os status</option>
                  <option value="PENDING">Pendente</option>
                  <option value="IN_PROGRESS">Em Andamento</option>
                  <option value="COMPLETED">Concluído</option>
                  <option value="CANCELLED">Cancelado</option>
                </select>
              </div>
              <div class="col-md-4">
                <select v-model="filters.exam_type" class="form-select">
                  <option value="">Todos os tipos</option>
                  <option value="XRAY">Raio-X</option>
                  <option value="CT">Tomografia</option>
                  <option value="MRI">Ressonância</option>
                  <option value="ULTRASOUND">Ultrassom</option>
                  <option value="MAMMOGRAPHY">Mamografia</option>
                  <option value="LABORATORIO">Laboratório</option>
                </select>
              </div>
              <div class="col-md-4">
                <div v-if="selectedExams.length > 0" class="d-flex align-items-center">
                  <span class="me-2 text-muted">{{ selectedExams.length }} selecionado(s)</span>
                  <div class="btn-group" role="group">
                    <button 
                      type="button" 
                      class="btn btn-primary btn-sm"
                      @click="viewSelectedExams"
                    >
                      <i class="bx bx-show me-1"></i>
                      Visualizar
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-outline-primary btn-sm"
                      @click="selectAllLaboratorio"
                      v-if="hasLaboratorioExams"
                    >
                      <i class="bx bx-check-square me-1"></i>
                      Selecionar Todos
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-outline-secondary btn-sm"
                      @click="clearSelection"
                    >
                      <i class="bx bx-x me-1"></i>
                      Limpar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-sm-12 col-md-6">
                <div id="exams-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Mostrar&nbsp;
                    <b-form-select v-model="perPage" size="sm" :options="pageOptions"></b-form-select>&nbsp;registros
                  </label>
                </div>
              </div>
              <!-- Search -->
              <div class="col-sm-12 col-md-6">
                <div id="exams-table_filter" class="dataTables_filter text-md-right">
                  <label class="d-inline-flex align-items-center">
                    Buscar:
                    <b-form-input
                      v-model="filter"
                      type="search"
                      class="form-control form-control-sm ml-2"
                      placeholder="ID, descrição..."
                    ></b-form-input>
                  </label>
                </div>
              </div>
              <!-- End search -->
            </div>

            <div v-if="loadingExams" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
              </div>
            </div>

            <div v-else class="table-responsive">
              <b-table
                :items="filteredExams"
                :fields="fields"
                responsive="sm"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                :per-page="perPage"
                :current-page="currentPage"
                @filtered="onFiltered"
              >
                <template v-slot:cell(select)="row">
                  <div v-if="row.item.exam_type === 'LABORATORIO'" class="text-center">
                    <input 
                      type="checkbox" 
                      class="form-check-input" 
                      :value="row.item.id"
                      v-model="selectedExams"
                    />
                  </div>
                </template>

                <template v-slot:cell(id)="row">
                  <router-link :to="`/exams/${row.value}`" class="text-primary">
                    {{ row.value }}
                  </router-link>
                </template>

                <template v-slot:cell(exam_type)="row">
                  <span class="badge bg-soft-primary text-primary">
                    {{ getExamTypeLabel(row.value) }}
                  </span>
                </template>

                <template v-slot:cell(status)="row">
                  <span :class="getStatusBadgeClass(row.value)">
                    {{ getStatusLabel(row.value) }}
                  </span>
                </template>

                <template v-slot:cell(scheduled_date)="row">
                  {{ formatDate(row.value) }}
                </template>

                <template v-slot:cell(description)="row">
                  <div class="text-truncate" style="max-width: 300px;" :title="row.value">
                    {{ row.value || 'Sem descrição' }}
                  </div>
                </template>

                <template v-slot:cell(action)="row">
                  <div class="d-flex gap-2">                    
                    <a 
                      v-if="hasStudyUID(row.item)"
                      href="javascript:void(0);"
                      class="text-info"
                      v-b-tooltip.hover
                      title="Ver DICOM"
                      @click="viewDicom(row.item)"
                    >
                      <i class="mdi mdi-eye font-size-18"></i>
                    </a>
                  </div>
                </template>
              </b-table>
            </div>

            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>

            <div v-if="filteredExams.length === 0 && !loadingExams" class="alert alert-info mt-3">
              <i class="bx bx-info-circle me-2"></i>
              {{ exams.length === 0 ? 'Nenhum exame encontrado para este paciente' : 'Nenhum exame corresponde aos filtros aplicados' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { examService } from "@/api/exams";
import { dicomService } from "@/api/dicom";

export default {
  page: {
    title: "Exames por Paciente",
    meta: [{ name: "description", content: "Exames por paciente" }],
  },
  components: { Layout, PageHeader },
  data() {
    return {
      title: "Exames por Paciente",
      items: [
        { text: "Exames", href: "/exams" },
        { text: "Por Paciente", active: true },
      ],
      patient: null,
      exams: [],
      filteredExams: [],
      loading: false,
      loadingExams: false,
      patientId: null,
      totalExams: 0,
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 25, 50],
      filter: null,
      filterOn: [],
      sortBy: "id",
      sortDesc: true,
      filters: {
        status: "",
        exam_type: "",
      },
      selectedExams: [],
      fields: [
        { key: "select", label: "", sortable: false, thClass: 'text-center', tdClass: 'text-center' },
        { key: "id", sortable: true, label: "ID" },
        { key: "exam_type", sortable: true, label: "Tipo" },
        { key: "scheduled_date", sortable: true, label: "Data Agendada" },
        { key: "status", sortable: true, label: "Status" },
        { key: "description", sortable: false, label: "Descrição" },
        { key: "action", label: "Ações" }
      ]
    };
  },
  async created() {
    // Obter ID do paciente da rota
    this.patientId = this.$route.params.patientId || this.$route.query.patient_id;
    
    if (this.patientId) {
      await this.loadPatientData();
    } else {
      this.$toast.error("ID do paciente não informado");
      this.$router.push("/exams");
    }
  },
  computed: {
    /**
     * Total no. of records for pagination
     */
    rows() {
      return this.filteredExams.length;
    },
    
    /**
     * Verifica se existem exames de laboratório na lista filtrada
     */
    hasLaboratorioExams() {
      return this.filteredExams.some(exam => exam.exam_type === 'LABORATORIO');
    },
    
    /**
     * Lista apenas os exames de laboratório da lista filtrada
     */
    laboratorioExams() {
      return this.filteredExams.filter(exam => exam.exam_type === 'LABORATORIO');
    }
  },
  beforeDestroy() {
    // Limpar timeout de busca se existir - não mais necessário
  },
  watch: {
    '$route'(to) {
      // Reagir a mudanças na rota
      const newPatientId = to.params.patientId || to.query.patient_id;
      if (newPatientId !== this.patientId) {
        this.patientId = newPatientId;
        this.loadPatientData();
      }
    },

    // Watch for page changes
    currentPage() {
      // A paginação é handled automaticamente pelo BootstrapVue
    },

    // Watch for perPage changes
    perPage() {
      this.currentPage = 1; // Reset to first page
    },
    
    // Filtrar automaticamente quando os filtros mudarem
    'filters.status'() {
      this.filterExams();
    },
    
    'filters.exam_type'() {
      this.filterExams();
    },

    // Limpar seleções quando mudar de paciente
    patientId() {
      this.selectedExams = [];
    }
  },
  methods: {
    async loadPatientData() {
      this.loading = true;
      this.loadingExams = true;
      
      try {
        const response = await examService.getExamsByPatient(this.patientId);
        
        if (response.data && response.data.success) {
          this.patient = response.data.patient;
          this.exams = response.data.exams || [];
          this.totalExams = response.data.count || this.exams.length;
          this.filterExams(); // Aplicar filtros iniciais
          
          // Atualizar o título da página com o nome do paciente
          if (this.patient && this.patient.name) {
            this.title = `Exames de ${this.patient.name}`;
          }
        } else {
          this.$toast.error("Erro ao carregar dados do paciente");
          this.patient = null;
          this.exams = [];
          this.filteredExams = [];
        }
      } catch (error) {
        console.error("Erro ao carregar dados do paciente:", error);
        this.$toast.error("Erro ao carregar dados do paciente");
        this.patient = null;
        this.exams = [];
        this.filteredExams = [];
      } finally {
        this.loading = false;
        this.loadingExams = false;
      }
    },

    filterExams() {
      let filtered = [...this.exams];
      
      // Filtrar por status
      if (this.filters.status) {
        filtered = filtered.filter(exam => exam.status === this.filters.status);
      }
      
      // Filtrar por tipo de exame
      if (this.filters.exam_type) {
        filtered = filtered.filter(exam => exam.exam_type === this.filters.exam_type);
      }
      
      this.filteredExams = filtered;
      this.totalRows = filtered.length;
      this.currentPage = 1; // Reset para primeira página quando filtrar
      
      // Limpar seleções se não há mais exames de laboratório visíveis
      this.cleanupSelection();
    },

    /**
     * Search the table data with search input
     */
    onFiltered(filteredItems) {
      // Para busca local com BootstrapVue
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    updatePaginatedExams() {
      // Método não mais necessário - removido pois o BootstrapVue 
      // handle a paginação automaticamente com :per-page e :current-page
    },

    goBack() {
      this.$router.go(-1);
    },

    createNewExam() {
      if (this.patient) {
        this.$router.push(`/exams/create?patient_id=${this.patient.id}`);
      }
    },

    viewExam(exam) {
      this.$router.push(`/exams/${exam.id}`);
    },

    editExam(exam) {
      this.$router.push(`/exams/${exam.id}/edit`);
    },

    hasStudyUID(exam) {
      return (exam.description && exam.description.includes('Study UID:')) ||
             (exam.result && exam.result.includes('Study UID:'));
    },

    async viewDicom(exam) {
      try {
        // Extrai Study UID da descrição ou resultado
        let studyUID = null;
        if (exam.description && exam.description.includes('Study UID:')) {
          studyUID = exam.description.match(/Study UID: ([^\s|]+)/)?.[1];
        } else if (exam.result && exam.result.includes('Study UID:')) {
          studyUID = exam.result.match(/Study UID: ([^\s|]+)/)?.[1];
        }
        
        if (!studyUID) {
          this.$toast.error("Study UID não encontrado para este exame");
          return;
        }

        // Buscar links do viewer
        const response = await dicomService.getViewerLink(studyUID);
        
        if (response.data && response.data.viewer_links && response.data.viewer_links.ohif_viewer) {
          let viewerUrl = response.data.viewer_links.ohif_viewer;
          
          // Obter token do usuário autenticado
          let token = this.$store.state.auth.currentUser?.access_token || 
                     localStorage.getItem('token') || 
                     localStorage.getItem('access_token') ||
                     sessionStorage.getItem('token');
          
          // Adicionar token à URL
          if (token) {
            const separator = viewerUrl.includes('?') ? '&' : '?';
            viewerUrl = `${viewerUrl}${separator}token=${token}`;
          }
          
          // Abrir em nova aba
          window.open(viewerUrl, '_blank');
        } else {
          this.$toast.error("Link do visualizador DICOM não encontrado");
        }
      } catch (error) {
        console.error("Erro ao obter link do visualizador DICOM:", error);
        this.$toast.error("Erro ao abrir visualizador DICOM");
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('pt-BR');
    },

    formatCPFDisplay(cpf) {
      if (!cpf) return '';
      // Remove caracteres não numéricos
      const cleaned = cpf.replace(/\D/g, '');
      
      // Aplica formatação XXX.XXX.XXX-XX
      if (cleaned.length === 11) {
        return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      }
      return cpf;
    },

    getStatusLabel(status) {
      const labels = {
        PENDING: 'Pendente',
        IN_PROGRESS: 'Em Andamento',
        COMPLETED: 'Concluído',
        CANCELLED: 'Cancelado',
      };
      return labels[status] || status;
    },

    getStatusBadgeClass(status) {
      const classes = {
        PENDING: 'badge bg-soft-warning text-warning',
        IN_PROGRESS: 'badge bg-soft-info text-info',
        COMPLETED: 'badge bg-soft-success text-success',
        CANCELLED: 'badge bg-soft-danger text-danger',
      };
      return classes[status] || 'badge bg-soft-secondary text-secondary';
    },

    getExamTypeLabel(examType) {
      const labels = {
        XRAY: 'Raio-X',
        CT: 'Tomografia',
        MRI: 'Ressonância',
        ULTRASOUND: 'Ultrassom',
        MAMMOGRAPHY: 'Mamografia',
        LABORATORIO: 'Laboratório',
      };
      return labels[examType] || examType;
    },

    /**
     * Seleciona todos os exames de laboratório visíveis
     */
    selectAllLaboratorio() {
      const laboratorioIds = this.laboratorioExams.map(exam => exam.id);
      
      // Adiciona IDs que não estão já selecionados
      laboratorioIds.forEach(id => {
        if (!this.selectedExams.includes(id)) {
          this.selectedExams.push(id);
        }
      });
    },

    /**
     * Limpa todas as seleções
     */
    clearSelection() {
      this.selectedExams = [];
    },

    /**
     * Remove da seleção exames que não estão mais visíveis
     */
    cleanupSelection() {
      const visibleLaboratorioIds = this.laboratorioExams.map(exam => exam.id);
      this.selectedExams = this.selectedExams.filter(id => visibleLaboratorioIds.includes(id));
    },

    /**
     * Verifica se um exame está selecionado
     */
    isExamSelected(examId) {
      return this.selectedExams.includes(examId);
    },

    /**
     * Processa exames selecionados (placeholder para futuras ações)
     */
    processSelectedExams() {
      if (this.selectedExams.length === 0) {
        this.$toast.warning("Nenhum exame selecionado");
        return;
      }

      const selectedExamObjects = this.filteredExams.filter(exam => 
        this.selectedExams.includes(exam.id)
      );

      console.log("Exames selecionados:", selectedExamObjects);
      
      // Aqui você pode implementar ações específicas para os exames selecionados
      // Por exemplo: gerar relatório, exportar, etc.
      this.$toast.success(`${this.selectedExams.length} exame(s) processado(s)`);
    },

    /**
     * Navega para a página de visualização dos exames selecionados
     */
    viewSelectedExams() {
      if (this.selectedExams.length === 0) {
        this.$toast.warning("Nenhum exame selecionado");
        return;
      }

      // Criar query string com os IDs dos exames selecionados
      const examIds = this.selectedExams.join(',');
      
      // Navegar para a página de visualização com os exames selecionados
      this.$router.push({
        path: '/exams/laboratory-report',
        query: {
          patient_id: this.patientId,
          exam_ids: examIds
        }
      });
    },
  },
};
</script>

<style scoped>
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Bootstrap Badge Colors */
.badge.bg-soft-warning {
  color: #f1b44c;
  background-color: rgba(241, 180, 76, 0.18);
}

.badge.bg-soft-info {
  color: #50a5f1;
  background-color: rgba(80, 165, 241, 0.18);
}

.badge.bg-soft-success {
  color: #34c38f;
  background-color: rgba(52, 195, 143, 0.18);
}

.badge.bg-soft-danger {
  color: #f46a6a;
  background-color: rgba(244, 106, 106, 0.18);
}

.badge.bg-soft-primary {
  color: #556ee6;
  background-color: rgba(85, 110, 230, 0.18);
}

.badge.bg-soft-secondary {
  color: #74788d;
  background-color: rgba(116, 120, 141, 0.18);
}

/* Text colors for badges */
.text-warning {
  color: #f1b44c !important;
}

.text-info {
  color: #50a5f1 !important;
}

.text-success {
  color: #34c38f !important;
}

.text-danger {
  color: #f46a6a !important;
}

.text-primary {
  color: #556ee6 !important;
}

.text-secondary {
  color: #74788d !important;
}

/* Card styling */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-title {
  color: #495057;
  font-weight: 600;
}

/* Patient info styling */
.d-flex.align-items-center strong {
  min-width: 120px;
  color: #6c757d;
}

/* Responsive table */
@media (max-width: 768px) {
  .text-truncate {
    max-width: 150px !important;
  }
  
  .d-flex.gap-2 {
    flex-direction: column;
    gap: 0.25rem !important;
  }
  
  .btn-sm {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
}

/* Checkbox styling */
.form-check-input {
  margin: 0;
  cursor: pointer;
}

.form-check-input:checked {
  background-color: #556ee6;
  border-color: #556ee6;
}

/* Selection info styling */
.text-muted {
  font-size: 0.875rem;
}

/* Button group spacing */
.btn-group .btn {
  margin-left: 0;
}

.btn-group .btn:not(:first-child) {
  margin-left: -1px;
}
</style>
